import {
    createContext,
    lazy,
    Suspense,
    useCallback,
    useContext,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { VideoPlayer } from '@rocco/components/video-player/video-player';
import type { OverlaySlide } from '~/ui-rocco/pages/detail-shared/DetailImageOverlay';

/** Marks an image the viewer can open, and carries what it needs to show it. */
export const ARTICLE_PHOTO_ATTR = 'data-article-photo';

/**
 * The player for a picture that stands for a film — the video banner.
 *
 * Muted, because it starts on its own: a browser refuses an unprompted play
 * with sound, and refusing it would leave the reader looking at a still they
 * had already pressed play on. The controls are there to turn it up.
 */
const articleVideo = (src: string) => (
    <VideoPlayer
        src={src}
        fill
        controls
        playsInline
        mode="auto"
        // A dead embed leaves the viewer standing rather than taking the
        // article down with it.
        onError={() => {}}
    />
);

// The same viewer every detail page opens its pictures in, loaded when the
// first picture is clicked rather than with the article.
const DetailImageOverlay = lazy(() =>
    import('~/ui-rocco/pages/detail-shared/DetailImageOverlay').then((m) => ({
        default: m.DetailImageOverlay,
    })),
);

const ArticlePhotoViewerContext = createContext<
    ((element: HTMLElement) => void) | null
>(null);

/**
 * Opens the article's photo viewer on the image that was clicked.
 *
 * Returns null outside a provider, so a module rendered on the legacy article —
 * which mounts no viewer — is simply not clickable rather than throwing.
 */
export const useArticlePhotoViewer = () =>
    useContext(ArticlePhotoViewerContext);

/**
 * Reads every openable image on the page, in document order.
 *
 * Collected from the DOM at the moment of the click rather than registered by
 * each figure as it renders: the images are spread across a dozen page-builder
 * module shapes at several nesting depths, and document order is exactly the
 * order the reader sees. A registry would have to reproduce that order and stay
 * in step with it as modules mount.
 */
const collectPhotos = (): { slides: OverlaySlide[]; nodes: HTMLElement[] } => {
    const nodes = Array.from(
        document.querySelectorAll<HTMLElement>(`[${ARTICLE_PHOTO_ATTR}]`),
    );
    return {
        nodes,
        slides: nodes.map((node) => ({
            src: node.dataset.articlePhotoSrc ?? '',
            // The library image behind the picture, so a save from the viewer
            // pins the photograph — the same thing the figure's own hover
            // Save pins. Without it the viewer falls back to the article, and
            // the two controls on one picture disagree.
            id: Number(node.dataset.articlePhotoId) || undefined,
            // The banner blanks its alt when a caption is shown beneath it,
            // so the caption stands in where the alt is empty.
            alt:
                node.getAttribute('alt') ||
                node.dataset.articlePhotoCaption ||
                '',
            // A picture that stands for a film opens as the film. `src` keeps
            // the still, so paging and the thumbnails still have something to
            // draw for this slide.
            video: node.dataset.articlePhotoVideo
                ? articleVideo(node.dataset.articlePhotoVideo)
                : undefined,
        })),
    };
};

interface ArticlePhotoViewerProviderProps {
    /** The item a save from the viewer lands on the design board as. */
    articleID: number;
    title: string;
    children: ReactNode;
}

/**
 * Holds the article's photo viewer, and hands its images the way to open it.
 *
 * One viewer for the whole article rather than one per figure: the reader pages
 * between the article's pictures once it is open, which means the set has to
 * outlive whichever figure was clicked.
 *
 * The viewer itself is the one the product and project pages open, so the
 * chrome — close, share, save, paging, zoom — is the same on every page.
 */
export const ArticlePhotoViewerProvider = ({
    articleID,
    title,
    children,
}: ArticlePhotoViewerProviderProps) => {
    const [slides, setSlides] = useState<OverlaySlide[]>([]);
    const [index, setIndex] = useState<number | null>(null);
    // Where the clicked picture sat, so the viewer opens out of it.
    const originRect = useRef<DOMRect | null>(null);

    const openFrom = useCallback((element: HTMLElement) => {
        const { slides: found, nodes } = collectPhotos();
        const at = nodes.indexOf(element);
        if (at === -1 || found.length === 0) return;
        originRect.current = element.getBoundingClientRect();
        setSlides(found);
        setIndex(at);
    }, []);

    const close = useCallback(() => setIndex(null), []);

    return (
        <ArticlePhotoViewerContext.Provider value={openFrom}>
            {children}
            {index !== null ? (
                <Suspense fallback={null}>
                    <DetailImageOverlay
                        slides={slides}
                        originRect={originRect.current}
                        index={index}
                        onIndexChange={setIndex}
                        onClose={close}
                        pinId={articleID}
                        pinType="ArticlePin"
                        title={title}
                    />
                </Suspense>
            ) : null}
        </ArticlePhotoViewerContext.Provider>
    );
};
